import { __awaiter } from "tslib";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect } from 'react';
import { Banner, Box, Divider, StackBlock } from '@yonderland/andes-react';
import { PickupPointSelected } from './PickupPointSelected';
import { PickupPointSelector } from './PickupPointSelector';
import { useDeliveryState, usePickupPointSearchContext } from '../../../../context/CheckoutDeliveryContext';
import { CustomerDetailsFields } from '../CustomerDetailsFields';
import { CustomerDetailsPreview } from '../CustomerDetailsPreview';
import { DeliveryOptionsEnum, Provider } from '@as-react/common-types';
import { useAemConfig } from '@as-react/common-components';
import { useTranslation } from 'react-i18next';
import { I18N_NAMESPACES } from '../../../../constants/i18nConstants';
import CustomerUtil from '../../../../util/CustomerUtil';
import { InvoiceFields } from '../../../form/invoice-fields/InvoiceFields';
import { AddressFieldsNL } from '../../../form/address-fields/address-fields-nl/AddressFieldsNL';
import { useCheckoutConfig } from '../../../../context/CheckoutContext';
import { useQueryClient } from '@tanstack/react-query';
import { checkoutQueries } from '@as-react/common-queries';
import { AddressPreview } from '../AddressPreview';
import { useValidateCustomerDetails } from '../useValidateCustomerDetails';
import { useFormContext } from 'react-hook-form';
import { useValidateAddress } from '../useValidateAddress';
import { isDisabledVatNumberField } from '../../../form/invoice-fields/invoiceFields.utils';
import { hasPrefilledAddress } from '../../../form/address-fields/addressFields.utils';
export const PickupPointContainer = () => {
    const { t } = useTranslation([I18N_NAMESPACES.buy, I18N_NAMESPACES.general]);
    const [{ info, errors, activeDeliveryOption, pickupPoint, openSelector }, dispatch] = useDeliveryState();
    const { deliveryForm: { hideInvoice }, } = useCheckoutConfig();
    const { setLocationSearch } = usePickupPointSearchContext();
    const { defaultRequestParams, servicesEndpoint, enableFullVatForm, buyDelivery } = useAemConfig();
    const isInvoiceEnabled = CustomerUtil.isInvoiceEnabled(hideInvoice, info);
    const { getPickupPoints } = checkoutQueries({ servicesEndpoint, defaultRequestParams });
    const { selectedPickupPoint } = pickupPoint;
    const { pickupInformation, country } = info;
    const isProviderPostNL = (activeDeliveryOption === null || activeDeliveryOption === void 0 ? void 0 : activeDeliveryOption.provider) === Provider.POSTNL;
    const { showCustomerDetailsForm, handleEdit: handleEditCustomerDetails } = useValidateCustomerDetails();
    const { trigger, formState, getValues } = useFormContext();
    const customerValues = getValues('customer');
    const address = customerValues
        ? Object.assign(Object.assign({}, customerValues === null || customerValues === void 0 ? void 0 : customerValues.address), CustomerUtil.createCustomerNames(customerValues)) : {};
    const { isValidAddress } = useValidateAddress({
        address,
        country: info.country,
        trigger,
        shouldValidate: isProviderPostNL,
    });
    const hasCustomerError = !!formState.errors.customer;
    const showCustomerForm = showCustomerDetailsForm || hasCustomerError || (isProviderPostNL && !isValidAddress);
    const queryClient = useQueryClient();
    const validateActivePickupPoint = (activeDeliveryOption, pickupInformation) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        try {
            const data = yield queryClient.fetchQuery(getPickupPoints({
                countryCode: country,
                provider: activeDeliveryOption.provider,
                postcode: pickupInformation.properties.customerPostalCode,
                deliveryDate: activeDeliveryOption.sendDate,
                limit: buyDelivery.maxPickupPoints,
            }));
            if (data.data) {
                const activePoint = (_a = data.data) === null || _a === void 0 ? void 0 : _a.find(point => point.id === pickupInformation.properties.locationId);
                if (activePoint) {
                    dispatch({ type: 'select_pickup_point', payload: activePoint });
                    return;
                }
            }
            dispatch({ type: 'set_open_selector', payload: 'pickup-point' });
            resetPickupPoint();
        }
        catch (_b) {
            dispatch({ type: 'set_open_selector', payload: undefined });
            resetPickupPoint();
        }
    });
    useEffect(() => {
        if (!country ||
            !pickupInformation ||
            !selectedPickupPoint ||
            !activeDeliveryOption ||
            (activeDeliveryOption === null || activeDeliveryOption === void 0 ? void 0 : activeDeliveryOption.name) !== DeliveryOptionsEnum.PICKUP_POINT) {
            return;
        }
        validateActivePickupPoint(activeDeliveryOption, pickupInformation);
    }, [country]);
    useEffect(() => {
        if (selectedPickupPoint ||
            !activeDeliveryOption ||
            (activeDeliveryOption === null || activeDeliveryOption === void 0 ? void 0 : activeDeliveryOption.name) !== DeliveryOptionsEnum.PICKUP_POINT) {
            return;
        }
        if (!pickupInformation && !selectedPickupPoint) {
            dispatch({ type: 'set_open_selector', payload: 'pickup-point' });
            return;
        }
        if (pickupInformation) {
            validateActivePickupPoint(activeDeliveryOption, pickupInformation);
        }
    }, [activeDeliveryOption, pickupInformation]);
    const resetPickupPoint = () => {
        setLocationSearch('');
        dispatch({ type: 'select_pickup_point', payload: null });
        dispatch({ type: 'highlight_pickup_point', payload: null });
    };
    return activeDeliveryOption && activeDeliveryOption.name === DeliveryOptionsEnum.PICKUP_POINT ? (_jsxs(_Fragment, { children: [selectedPickupPoint && (_jsxs(StackBlock, { gap: "4", children: [_jsx(Box, { paddingBlockStart: "2", children: _jsxs(StackBlock, { gap: showCustomerForm ? '4' : undefined, children: [_jsx(PickupPointSelected, {}), _jsx(Divider, {}), showCustomerForm ? (_jsxs(StackBlock, { gap: "4", spacingClasses: "a:w-full", children: [_jsx(CustomerDetailsFields, { prefix: "customer." }), isProviderPostNL && (_jsxs(_Fragment, { children: [_jsx(Divider, {}), _jsx(AddressFieldsNL, { prefix: "customer.address." })] }))] })) : isProviderPostNL && hasPrefilledAddress(address) ? (_jsx(AddressPreview, { isValid: isValidAddress, address: address, onEdit: handleEditCustomerDetails })) : (_jsx(CustomerDetailsPreview, { onEdit: handleEditCustomerDetails })), isInvoiceEnabled && (_jsx(Box, { paddingBlockStart: "4", children: _jsx(InvoiceFields, { showPreview: true, country: info.country, fullVatFormEnabled: enableFullVatForm, disableVatNumberField: isDisabledVatNumberField(info) }) }))] }) }), activeDeliveryOption && errors[activeDeliveryOption.name] && (_jsx(Banner, { text: t(errors[activeDeliveryOption.name]), status: "error" }))] })), openSelector === 'pickup-point' && _jsx(PickupPointSelector, {})] })) : null;
};
